/**
 * Compra dentro de contrato vigente (ADR 0102, seção 3). O comprador define a modalidade na triagem;
 * a compra adicional dentro de contrato só segue com o contrato elegível e a cobertura confirmada
 * pelos dados estruturados do contrato (ADR 0030). Sem dado para confirmar, não há cobertura: o
 * caminho fica bloqueado e a compra vai à avaliação jurídica (aditivo F19) ou à compra nova.
 * Nada aqui muda o contrato: é só a conferência, usada pela API e pelas telas.
 */
import { z } from 'zod';
import { toScaled } from './money.ts';
import { uuid } from './schemas.ts';

/** Modalidades de contratação (o objeto da compra é o tipo; a modalidade é como se contrata). */
export const PURCHASE_CONTRACTING_MODES = [
  'new_purchase',
  'within_contract',
  'contract_billing',
] as const;
export type PurchaseContractingMode = (typeof PURCHASE_CONTRACTING_MODES)[number];

/**
 * O que o comprador define na triagem: compra nova ou dentro de um contrato vigente. A cobrança de
 * contrato encerra a solicitação pelo cancelamento (`contractBilling` na mudança de situação).
 */
export const purchaseContractingModeInput = z
  .object({
    mode: z.enum(['new_purchase', 'within_contract']),
    contractId: uuid.nullable().optional(),
  })
  .refine((v) => v.mode !== 'within_contract' || !!v.contractId, {
    message: 'Escolha o contrato vigente',
    path: ['contractId'],
  });
export type PurchaseContractingModeInput = z.infer<typeof purchaseContractingModeInput>;

/** O mínimo do contrato que Compras lê do Jurídico (legal.contracts_for_purchasing). */
export interface ContractForPurchase {
  id: string;
  code: string;
  title: string;
  legalEntityId: string;
  partnerId: string | null;
  partnerName: string;
  partnerActive: boolean;
  status: string;
  startDate: string | null;
  endDate: string | null;
  amount: string | null;
  currency: string | null;
  periodicity: string | null;
  valueStatus: string | null;
  valueNature: string | null;
  valueBasis: string | null;
  amendments: number;
}

/** Por que o contrato não serve para a compra dentro de contrato. */
export type ContractEligibilityIssue =
  /** Contrato de outra empresa do grupo: nunca. */
  | 'other_entity'
  /** Importado e ainda a confirmar pelo Jurídico. */
  | 'unconfirmed'
  /** Fora de "vigente" (em assinatura, encerrado...). */
  | 'not_active'
  /** Sem início, ainda não começou ou já terminou. */
  | 'not_in_force'
  /** Sem fornecedor do cadastro ligado. */
  | 'no_partner'
  /** Fornecedor inativo no cadastro. */
  | 'partner_inactive'
  /** O fornecedor da compra não é o do contrato. */
  | 'other_partner'
  /** Sem valor definido, montante, moeda ou base para conferir a cobertura. */
  | 'insufficient_data';

/**
 * Elegibilidade do contrato (todas as condições). Estar "vigente" no Jurídico não basta: precisa
 * estar dentro das datas, com fornecedor válido e com dados para conferir a cobertura.
 * O fornecedor da compra entra quando já é conhecido (proposta escolhida, pedido).
 */
export function contractEligibilityIssues(
  c: ContractForPurchase,
  purchase: { legalEntityId: string; partnerId?: string | null },
  today: string,
): ContractEligibilityIssue[] {
  const out: ContractEligibilityIssue[] = [];
  if (c.legalEntityId !== purchase.legalEntityId) out.push('other_entity');
  if (c.status === 'unconfirmed') out.push('unconfirmed');
  else if (c.status !== 'active') out.push('not_active');
  if (!c.startDate || c.startDate > today || (c.endDate != null && c.endDate < today)) {
    out.push('not_in_force');
  }
  if (!c.partnerId) out.push('no_partner');
  else if (!c.partnerActive) out.push('partner_inactive');
  else if (purchase.partnerId && purchase.partnerId !== c.partnerId) out.push('other_partner');
  if (c.valueStatus !== 'defined' || !c.amount || !c.currency || !c.valueBasis) {
    out.push('insufficient_data');
  }
  return out;
}

/**
 * Cobertura da compra pelo contrato (D5, extrapolação):
 * - covered: o contrato registra um teto (valor total fixo) e a compra, somada aos pedidos já
 *   ligados ao contrato, cabe nele;
 * - exceeds: passa do teto registrado: extrapolação, só com aditivo (F19), nunca com justificativa;
 * - unverifiable: os dados do contrato não permitem confirmar. Valor por período, por entrega,
 *   estimado, variável ou royalties não têm teto nem preço unitário estruturado; contrato com aditivo
 *   registrado não tem o valor consolidado; moeda diferente não se compara. Vai à avaliação jurídica.
 * Valor acima do valor periódico não é, por si, extrapolação: é cobertura não confirmável.
 */
export type ContractCoverageReason =
  | 'within_ceiling'
  | 'above_ceiling'
  | 'value_undefined'
  | 'currency_mismatch'
  | 'amendments'
  | 'no_ceiling';
export interface ContractCoverage {
  status: 'covered' | 'exceeds' | 'unverifiable';
  reason: ContractCoverageReason;
  /** Teto registrado e quanto já foi pedido por Compras dentro dele (só com teto). */
  ceiling: string | null;
  consumed: string | null;
}

export function contractCoverage(
  c: Pick<
    ContractForPurchase,
    'amount' | 'currency' | 'valueStatus' | 'valueNature' | 'valueBasis' | 'amendments'
  >,
  purchase: { total: string; currency: string; consumed: string },
): ContractCoverage {
  const result = (status: ContractCoverage['status'], reason: ContractCoverageReason) => ({
    status,
    reason,
    ceiling: reason === 'within_ceiling' || reason === 'above_ceiling' ? c.amount : null,
    consumed: reason === 'within_ceiling' || reason === 'above_ceiling' ? purchase.consumed : null,
  });
  if (c.valueStatus !== 'defined' || !c.amount || !c.currency) {
    return result('unverifiable', 'value_undefined');
  }
  if (c.amendments > 0) return result('unverifiable', 'amendments');
  if (c.valueBasis !== 'total' || c.valueNature !== 'fixed') {
    return result('unverifiable', 'no_ceiling');
  }
  if (c.currency !== purchase.currency) return result('unverifiable', 'currency_mismatch');
  const used = toScaled(purchase.consumed) + toScaled(purchase.total);
  return used <= toScaled(c.amount)
    ? result('covered', 'within_ceiling')
    : result('exceeds', 'above_ceiling');
}

/** Justificativa gravada sozinha na escolha da proposta da compra dentro de contrato. */
export function withinContractJustification(contractCode: string): string {
  return `Compra dentro do contrato vigente ${contractCode}: cotação dispensada pela cobertura do contrato.`;
}
